// Winter Volleyball Championship squad flyer — Court Rally edition.
//
// Rebuilt (2026-10-20) in the sports-poster idiom the captain asked
// for: dark stadium ground, chrome-silver headline over a big
// volleyball mark, blue + red team crests meeting over a VS bolt, two
// roster panels color-coded to each side, gold-icon info bar, and a
// "LET THE MATCH BEGIN!" tagline. The prop contract matches the old
// Heraldic flyer so Squads.jsx wiring is unchanged.
//
// The poster is screenshotted via html2canvas, so the CSS stays on
// solid colors + simple gradients (no mask-image, no complex filters)
// and the big headline uses text-stroke + layered text-shadow for the
// metal/outline look html2canvas can actually render.
//
// Team A crest uses the real Gabruz Universe logo (/logo.png). Team B
// gets a text crest ("B") since there is no Baazigars mark in-app.

import React from "react";
import {
  CalendarDays, Clock, MapPin, Droplet, Banana, Crown
} from "lucide-react";
import { formatDateTemplate, formatTimeTemplate, splitVenue } from "./flyerHelpers";

// Render a Mikasa-style stylized volleyball: three curved panel lines
// over a gold-and-navy sphere. Inline SVG so html2canvas captures it
// without a round-trip through a bitmap.
function VolleyballMark({ size = 190 }) {
  return (
    <svg
      className="vbp-ball"
      viewBox="0 0 120 120"
      width={size}
      height={size}
      aria-hidden="true"
    >
      <defs>
        <radialGradient id="vbp-ball-g" cx="40%" cy="35%" r="75%">
          <stop offset="0%"  stopColor="#FFFFFF" stopOpacity="0.35"/>
          <stop offset="60%" stopColor="#FFFFFF" stopOpacity="0"/>
        </radialGradient>
      </defs>
      <circle cx="60" cy="60" r="54" fill="#F0C34A" stroke="#102257" strokeWidth="3"/>
      <path d="M60,6 C78,42 78,78 60,114" stroke="#102257" strokeWidth="3" fill="none"/>
      <path d="M9,42 C42,30 78,30 111,42" stroke="#102257" strokeWidth="3" fill="none"/>
      <path d="M9,78 C42,90 78,90 111,78" stroke="#102257" strokeWidth="3" fill="none"/>
      <circle cx="60" cy="60" r="54" fill="url(#vbp-ball-g)"/>
    </svg>
  );
}

// Each roster is sorted alphabetically so the flyer reads the same
// regardless of pick order; captain gets a small "(C)" marker.
function sortRoster(players) {
  return [...(players || [])].sort((a, b) =>
    (a.name || "").localeCompare(b.name || "", undefined, { sensitivity: "base" })
  );
}

function TeamPanel({ side, label, teamName, players, captainId }) {
  const sorted = sortRoster(players);
  return (
    <section className={`vbp-team vbp-team--${side}`}>
      <header className="vbp-team-head">
        <small>{label}</small>
        <b>{teamName}</b>
      </header>
      <ul className="vbp-team-list">
        {sorted.length === 0 && (
          <li className="vbp-team-empty">No players named yet.</li>
        )}
        {sorted.map((p, i) => {
          const isCap = !!p.id && p.id === captainId;
          return (
            <li key={p.id || `${side}-${i}`}>
              <span className="vbp-bullet" aria-hidden="true"/>
              <span className="vbp-player">
                {p.name}
                {isCap && <em className="vbp-cap"> (C)</em>}
              </span>
            </li>
          );
        })}
      </ul>
    </section>
  );
}

export function SummerVolleyballFlyer({
  match, reportingTime, venue,
  startingPlayers, teamBStartingPlayers,
  benchPlayers, teamBBenchPlayers,
  snacksVolunteer, alcoholVolunteer,
  captainPickId = null
}) {
  const { short: venueShort } = splitVenue(venue);
  const dateStr    = formatDateTemplate(match?.date)   || "TBD";
  const reportStr  = formatTimeTemplate(reportingTime) || "TBD";
  const groundStr  = venueShort || "TBD";
  const showVolunteers = !!(snacksVolunteer || alcoholVolunteer);

  // Date pill: "SAT" + "NOV 15" split so the pill reads at a glance.
  const dayShort = (() => {
    if (!match?.date) return { day: "TBD", rest: "" };
    const d = new Date(match.date + "T12:00:00");
    if (isNaN(d)) return { day: "TBD", rest: match.date };
    const day  = d.toLocaleDateString("en-US", { weekday: "short" }).toUpperCase();
    const rest = d.toLocaleDateString("en-US", { month: "short", day: "numeric" }).toUpperCase();
    return { day, rest };
  })();

  return (
    <div className="poster vbPoster">

      {/* ── Headline: GABRUZ UNIVERSE wordmark with inline volleyball,
            then the "Winter Volleyball Championship" ribbon ── */}
      <header className="vbp-header">
        <div className="vbp-crown" aria-hidden="true"><Crown size={44} strokeWidth={2.5}/></div>
        <h1 className="vbp-title">
          <span className="vbp-title-row vbp-title-row--gold">GABRUZ</span>
          <span className="vbp-title-ball" aria-hidden="true">
            <VolleyballMark size={54}/>
          </span>
          <span className="vbp-title-row vbp-title-row--silver">UNIVERSE</span>
        </h1>
        <div className="vbp-ribbon">
          <span className="vbp-star">★</span>
          WINTER VOLLEYBALL CHAMPIONSHIP
          <span className="vbp-star">★</span>
        </div>
      </header>

      {/* ── Painted mascot crests meeting over a VS bolt. Both PNGs
            carry the shield + team name baked into the art, so no
            extra frame or caption around them. ── */}
      <section className="vbp-vs">
        <div className="vbp-crest vbp-crest--a">
          <img
            src="/pitt-gabruz-crest.jpg"
            alt="Pitt Gabruz"
            className="vbp-crest-art"
          />
        </div>

        <div className="vbp-vs-bolt">
          <span className="vbp-vs-bolt-txt">VS</span>
        </div>

        <div className="vbp-crest vbp-crest--b">
          <img
            src="/pitt-baazigars-crest.png"
            alt="Pitt Baazigars"
            className="vbp-crest-art"
          />
        </div>
      </section>

      {/* ── Two roster panels, blue vs red ── */}
      <div className="vbp-rosters">
        <TeamPanel
          side="a"
          label="TEAM A"
          teamName="Pitt Gabruz"
          players={startingPlayers}
          captainId={captainPickId}
        />
        <TeamPanel
          side="b"
          label="TEAM B"
          teamName="Pitt Baazigars"
          players={teamBStartingPlayers}
          captainId={null}
        />
      </div>

      {/* ── Info bar: date · venue · reporting ── */}
      <div className="vbp-info">
        <div className="vbp-info-cell">
          <CalendarDays size={22}/>
          <div>
            <small>{dayShort.day}</small>
            <b>{dayShort.rest || dateStr}</b>
          </div>
        </div>
        <div className="vbp-info-sep" aria-hidden="true"/>
        <div className="vbp-info-cell">
          <MapPin size={22}/>
          <b className="vbp-info-venue">{groundStr}</b>
        </div>
        <div className="vbp-info-sep" aria-hidden="true"/>
        <div className="vbp-info-cell">
          <Clock size={22}/>
          <div>
            <small>REPORTING</small>
            <b>{reportStr}</b>
          </div>
        </div>
      </div>

      {/* ── Volunteers ── */}
      {showVolunteers && (
        <div className="vbp-vol">
          <div className="vbp-vol-item">
            <span className="vbp-vol-icon"><Droplet size={20}/></span>
            <div>
              <small>SNACKS</small>
              <b>{snacksVolunteer || "TBD"}</b>
            </div>
          </div>
          <div className="vbp-vol-item">
            <span className="vbp-vol-icon"><Banana size={20}/></span>
            <div>
              <small>BEVERAGES</small>
              <b>{alcoholVolunteer || "TBD"}</b>
            </div>
          </div>
        </div>
      )}

      {/* ── Tagline ── */}
      <div className="vbp-tagline">
        LET THE MATCH <em>BEGIN!</em>
      </div>

    </div>
  );
}
